import React, { useRef } from 'react';

import type { FrapiTeaserItemSized } from '../../../types/frapi-types.js';
import type { TeaserListProps } from './TeaserList.js';
import type { TeaserSavedProps } from './TeaserSaved.js';

import { getPulseAttributesFromElement } from '../../../../core/mandatory-integrations/pulse/utils/index.js';
import {
  useByfunnPulseContext,
  buildEngagementClickTeaserInputParams,
  buildImpressionTeaserAttributes,
} from '../../../pulse/index.js';
import { getPulseMeta } from './pulseMeta.js';
import { getTeaserClickTrackingObject } from './teaser-click-tracking.js';

export type WrappedTeaser =
  | FrapiTeaserItemSized
  | TeaserSavedProps
  | TeaserListProps;

interface TeaserWrapperProps {
  teaser: WrappedTeaser;
  classNames: string;
  children: React.ReactNode;
  link?: string;
  isMobile?: boolean;
  pageType?: string;
  bundleId?: string;
  externalSource?: string;
}

const getTargetUrl = (url: string, path: string) => {
  const urlOrigin = new URL(url).origin;

  return `${urlOrigin}${path}`;
};

const generatePageId = (page: string, pageType?: string) => {
  if (pageType) return pageType;
  switch (page) {
    case '/':
      return 'frontpage';
    case '/search':
      return 'search';
  }

  return 'section-page';
};

const TeaserWrapper: React.FC<TeaserWrapperProps> = ({
  teaser,
  classNames,
  children,
  link,
  isMobile,
  pageType,
  externalSource,
}) => {
  const href = link || `/artikkel/${teaser.id}/${teaser.slug}`;
  const refToLink = 'innerRef' in teaser ? teaser.innerRef : null;

  const { trackClickTeaser } = useByfunnPulseContext();
  const articleRef = useRef(null);

  const handleClick = (): void => {
    if (!articleRef.current) {
      return;
    }

    const pulseAttributes = getPulseAttributesFromElement(articleRef.current);

    if (!pulseAttributes.entityId) {
      return;
    }

    const targetUrl = link || getTargetUrl(window.location.href, href);

    const legacy = getTeaserClickTrackingObject({
      teaser,
      pulseAttributes,
      entityId: pulseAttributes.entityId,
      targetUrl,
      pageId: generatePageId(window.location.pathname, pageType),
      isMobile,
      externalSource,
    });

    const tpaas = buildEngagementClickTeaserInputParams(teaser, href);

    trackClickTeaser(legacy, tpaas);
  };

  return (
    <article
      className={classNames}
      {...getPulseMeta(teaser, href)}
      {...buildImpressionTeaserAttributes(teaser, href)}
      ref={articleRef}
    >
      <a
        className="teaser-link"
        href={href}
        ref={refToLink}
        onClick={handleClick}
      >
        {children}
      </a>
    </article>
  );
};

export default TeaserWrapper;
